import { type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WebFilterCriterionKind,
  WebFilterDateRangeMode,
  WebFilterTransport,
} from '@/api/generated/schemas';
import {
  Button,
  Checkbox,
  DateBox,
  DropdownMenu,
  Input,
  MenuItem,
  MenuLabel,
  Panel,
  SegmentedButtons,
  Select,
} from '@/shared/ui';
import { CatalogIdsSelector } from '../../internal/CatalogIdsSelector';
import {
  destinationSelector,
  type WebFilterDestinationRef,
} from '../../internal/destinationSelector';
import { productSelector, type WebFilterProductRef } from '../../internal/productSelector';
import {
  productTypeSelector,
  type WebFilterProductTypeRef,
} from '../../internal/productTypeSelector';
import { stationSelector, type WebFilterStationRef } from '../../internal/stationSelector';
import {
  asCatalogIds,
  asDates,
  asNumberRange,
  asTransportIds,
  CRITERION_DEFS,
  CRITERION_GROUPS,
  emptyCriterion,
  rangeToSeatsCompare,
  seatsCompareToRange,
  sortCriteria,
  type NumberRangeValues,
  type SeatsCompareOp,
} from '../../internal/criterionModel';
import type { WebFilterFormValues } from '../webFilterFormSchema';
import type { WebFilterFormApi } from '../useWebFilterForm';

type CriterionItem = WebFilterFormValues['criteria'][number];
type TransportChoice = WebFilterTransport | 'all';

export interface ProductFilterPanelProps {
  form: WebFilterFormApi;
}

export function ProductFilterPanel({ form }: ProductFilterPanelProps) {
  const { t } = useTranslation('www');

  return (
    <form.Field name="criteria">
      {(field) => {
        const criteria = field.state.value;
        const used = new Set(criteria.map((item) => item.kind));
        const ordered = sortCriteria(criteria);

        const setCriteria = (next: CriterionItem[]) => {
          field.handleChange(sortCriteria(next));
        };

        const patch = (kind: WebFilterCriterionKind, values: CriterionItem['values']) => {
          setCriteria(criteria.map((item) => (item.kind === kind ? { ...item, values } : item)));
        };

        return (
          <Panel
            variant="filled"
            icon="filter"
            title={t('webFilter.criteria.title')}
            subtitle={t('webFilter.criteria.subtitle')}
            actions={
              <DropdownMenu
                align="end"
                trigger={
                  <Button type="button" variant="ghost" size="sm" iconRight="chevdown">
                    {t('webFilter.criteria.add')}
                  </Button>
                }
              >
                {CRITERION_GROUPS.map((group) => (
                  <div key={group}>
                    <MenuLabel>{t(`webFilter.criteria.groups.${group}`)}</MenuLabel>
                    {CRITERION_DEFS.filter((def) => def.group === group).map((def) => {
                      const added = used.has(def.kind);
                      return (
                        <MenuItem
                          key={def.kind}
                          disabled={added}
                          onClick={() => {
                            if (added) return;
                            setCriteria([...criteria, emptyCriterion(def.kind)]);
                          }}
                        >
                          {t(`webFilter.criteria.kinds.${kindKey(def.kind)}`)}
                        </MenuItem>
                      );
                    })}
                  </div>
                ))}
              </DropdownMenu>
            }
          >
            {ordered.length === 0 ? (
              <p className="text-phx-sm text-phx-text-muted">{t('webFilter.criteria.empty')}</p>
            ) : (
              <div className="gap-phx-4 flex flex-col">
                {ordered.map((item) => (
                  <CriterionRow
                    key={item.kind}
                    item={item}
                    onChange={(values) => patch(item.kind, values)}
                    onRemove={() => setCriteria(criteria.filter((c) => c.kind !== item.kind))}
                  />
                ))}
              </div>
            )}
          </Panel>
        );
      }}
    </form.Field>
  );
}

function CriterionRow({
  item,
  onChange,
  onRemove,
}: {
  item: CriterionItem;
  onChange: (values: CriterionItem['values']) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('www');
  const def = CRITERION_DEFS.find((d) => d.kind === item.kind);

  return (
    <div className="border-phx-border-1 gap-phx-2 pt-phx-4 flex flex-col border-t first:border-t-0 first:pt-0">
      <div className="gap-phx-3 flex items-center justify-between">
        <div className="text-phx-sm text-phx-text font-semibold">
          {t(`webFilter.criteria.kinds.${kindKey(item.kind)}`)}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          {t('webFilter.criteria.remove')}
        </Button>
      </div>
      {def?.editor === 'transport' ? (
        <TransportEditor
          values={asTransportIds(item.values)}
          onChange={(ids) => onChange({ ids })}
        />
      ) : null}
      {def?.editor === 'catalog' ? (
        <CatalogEditor
          kind={item.kind}
          ids={asCatalogIds(item.values)}
          onChange={(ids) => onChange({ ids })}
        />
      ) : null}
      {def?.editor === 'range' ? (
        <RangeEditor
          values={asNumberRange(item.values)}
          unit={t(`webFilter.criteria.units.${kindKey(item.kind)}`)}
          onChange={onChange}
        />
      ) : null}
      {def?.editor === 'seats' ? (
        <SeatsEditor values={asNumberRange(item.values)} onChange={onChange} />
      ) : null}
      {def?.editor === 'published' ? (
        <PublishedEditor values={asNumberRange(item.values)} onChange={onChange} />
      ) : null}
      {def?.editor === 'dates' ? (
        <DatesEditor values={asDates(item.values)} onChange={onChange} />
      ) : null}
    </div>
  );
}

function TransportEditor({
  values,
  onChange,
}: {
  values: WebFilterTransport[];
  onChange: (ids: WebFilterTransport[]) => void;
}) {
  const { t } = useTranslation('www');
  const isAll =
    values.length === 0 ||
    (values.includes(WebFilterTransport.Air) && values.includes(WebFilterTransport.Bus));
  const value: TransportChoice = isAll ? 'all' : (values[0] ?? 'all');

  return (
    <SegmentedButtons<TransportChoice>
      value={value}
      options={[
        { value: 'all', label: t('webFilter.criteria.transport.all') },
        { value: WebFilterTransport.Air, label: t('webFilter.criteria.transport.air') },
        { value: WebFilterTransport.Bus, label: t('webFilter.criteria.transport.bus') },
      ]}
      onChange={(next) => onChange(next === 'all' ? [] : [next])}
    />
  );
}

function destinationId(item: WebFilterDestinationRef) {
  return item.destinationId;
}

function productTypeId(item: WebFilterProductTypeRef) {
  return item.id;
}

function stationId(item: WebFilterStationRef) {
  return item.stationId;
}

function productId(item: WebFilterProductRef) {
  return item.productId;
}

function CatalogEditor({
  kind,
  ids,
  onChange,
}: {
  kind: WebFilterCriterionKind;
  ids: number[];
  onChange: (ids: number[]) => void;
}) {
  const { t } = useTranslation('www');
  const label = t('webFilter.criteria.selectors.pick');
  const placeholder = t('webFilter.criteria.catalog.all');

  if (kind === WebFilterCriterionKind.Destination) {
    return (
      <CatalogIdsSelector
        selector={destinationSelector}
        ids={ids}
        onChange={onChange}
        getId={destinationId}
        label={label}
        placeholder={placeholder}
      />
    );
  }

  if (kind === WebFilterCriterionKind.ProductTypes) {
    return (
      <CatalogIdsSelector
        selector={productTypeSelector}
        ids={ids}
        onChange={onChange}
        getId={productTypeId}
        label={label}
        placeholder={placeholder}
      />
    );
  }

  if (kind === WebFilterCriterionKind.Departure) {
    return (
      <CatalogIdsSelector
        selector={stationSelector}
        ids={ids}
        onChange={onChange}
        getId={stationId}
        extraFilters={[{ field: 'domestic', text: 'true' }]}
        label={label}
        placeholder={placeholder}
      />
    );
  }

  if (kind === WebFilterCriterionKind.Products) {
    return (
      <CatalogIdsSelector
        selector={productSelector}
        ids={ids}
        onChange={onChange}
        getId={productId}
        label={label}
        placeholder={placeholder}
      />
    );
  }

  return null;
}

function RangeEditor({
  values,
  unit,
  onChange,
}: {
  values: NumberRangeValues;
  unit: string;
  onChange: (values: NumberRangeValues) => void;
}) {
  const { t } = useTranslation('www');
  return (
    <div className="gap-phx-2 flex flex-wrap items-center">
      <NumberInput
        value={values.from}
        onChange={(from) => onChange({ ...values, from })}
        aria-label={t('webFilter.criteria.from')}
      />
      <span className="text-phx-sm text-phx-text-muted">{t('webFilter.criteria.to')}</span>
      <NumberInput
        value={values.to}
        onChange={(to) => onChange({ ...values, to })}
        aria-label={t('webFilter.criteria.to')}
      />
      <span className="text-phx-sm text-phx-text-muted">{unit}</span>
    </div>
  );
}

function SeatsEditor({
  values,
  onChange,
}: {
  values: NumberRangeValues;
  onChange: (values: NumberRangeValues) => void;
}) {
  const { t } = useTranslation('www');
  const { op, value } = rangeToSeatsCompare(values);
  return (
    <div className="gap-phx-2 flex flex-wrap items-center">
      <Select
        className="w-[180px]"
        size="sm"
        value={op}
        options={[
          { value: '', label: t('webFilter.criteria.seats.none') },
          { value: 'lt', label: t('webFilter.criteria.seats.lt') },
          { value: 'gt', label: t('webFilter.criteria.seats.gt') },
        ]}
        onChange={(event) => {
          const next = event.target.value as SeatsCompareOp;
          onChange(seatsCompareToRange(next, next === '' ? null : (value ?? 0)));
        }}
      />
      {op ? (
        <>
          <NumberInput
            value={value}
            onChange={(n) => onChange(seatsCompareToRange(op, n))}
            aria-label={t('webFilter.criteria.seats.value')}
          />
          <span className="text-phx-sm text-phx-text-muted">
            {t('webFilter.criteria.units.seats')}
          </span>
        </>
      ) : null}
    </div>
  );
}

function PublishedEditor({
  values,
  onChange,
}: {
  values: NumberRangeValues;
  onChange: (values: NumberRangeValues) => void;
}) {
  const { t } = useTranslation('www');
  return (
    <div className="gap-phx-2 flex flex-wrap items-center">
      <span className="text-phx-sm text-phx-text-muted">
        {t('webFilter.criteria.published.prefix')}
      </span>
      <NumberInput
        value={values.to}
        onChange={(to) => onChange({ from: null, to })}
        aria-label={t('webFilter.criteria.published.value')}
      />
      <span className="text-phx-sm text-phx-text-muted">
        {t('webFilter.criteria.published.suffix')}
      </span>
    </div>
  );
}

function DatesEditor({
  values,
  onChange,
}: {
  values: ReturnType<typeof asDates>;
  onChange: (values: ReturnType<typeof asDates>) => void;
}) {
  const { t } = useTranslation('www');
  return (
    <div className="gap-phx-3 flex flex-col">
      {values.ranges.map((range, index) => (
        <div key={index} className="gap-phx-2 flex flex-wrap items-end">
          <Select
            className="w-[220px]"
            size="sm"
            value={range.mode}
            options={[
              {
                value: WebFilterDateRangeMode.Overlap,
                label: t('webFilter.criteria.dates.overlap'),
              },
              {
                value: WebFilterDateRangeMode.Departures,
                label: t('webFilter.criteria.dates.departures'),
              },
            ]}
            onChange={(event) => {
              const mode = event.target.value as WebFilterDateRangeMode;
              onChange({
                ...values,
                ranges: values.ranges.map((item, i) => (i === index ? { ...item, mode } : item)),
              });
            }}
          />
          <DateBox
            value={range.from}
            onChange={(from) => {
              onChange({
                ...values,
                ranges: values.ranges.map((item, i) => (i === index ? { ...item, from } : item)),
              });
            }}
            fieldWidth={150}
            size="sm"
          />
          <span className="text-phx-sm text-phx-text-muted pb-phx-2">–</span>
          <DateBox
            value={range.to}
            onChange={(to) => {
              onChange({
                ...values,
                ranges: values.ranges.map((item, i) => (i === index ? { ...item, to } : item)),
              });
            }}
            fieldWidth={150}
            size="sm"
          />
          <Input
            className="w-[160px]"
            size="sm"
            value={range.label ?? ''}
            placeholder={t('webFilter.criteria.dates.labelPlaceholder')}
            onChange={(event) => {
              const label = event.target.value.trim() ? event.target.value : null;
              onChange({
                ...values,
                ranges: values.ranges.map((item, i) => (i === index ? { ...item, label } : item)),
              });
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              onChange({ ...values, ranges: values.ranges.filter((_, i) => i !== index) })
            }
          >
            ✕
          </Button>
        </div>
      ))}
      <Checkbox
        label={t('webFilter.criteria.dates.weekend')}
        checked={values.weekend}
        onCheckedChange={(weekend) => onChange({ ...values, weekend })}
      />
      <Checkbox
        label={t('webFilter.criteria.dates.holiday')}
        checked={values.holiday}
        onCheckedChange={(holiday) => onChange({ ...values, holiday })}
      />
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              ...values,
              ranges: [
                ...values.ranges,
                { mode: WebFilterDateRangeMode.Overlap, from: '', to: '', label: null },
              ],
            })
          }
        >
          {t('webFilter.criteria.dates.addRange')}
        </Button>
      </div>
      <p className="text-phx-xs text-phx-text-muted">{t('webFilter.criteria.dates.hint')}</p>
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  ...rest
}: {
  value: number | null;
  onChange: (value: number | null) => void;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'>) {
  return (
    <Input
      type="number"
      min={0}
      className="w-[90px]"
      size="sm"
      value={value ?? ''}
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        const parsed = Number(raw);
        onChange(Number.isFinite(parsed) ? parsed : null);
      }}
      {...rest}
    />
  );
}

function kindKey(kind: WebFilterCriterionKind): string {
  return kind.charAt(0).toLowerCase() + kind.slice(1);
}
